import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config';
import { IconAlert, IconList } from '../components/Icons';
import './AdminFeedback.css';
import './AdminLogs.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

const PAGE_SIZE = 30;

function formatValue(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_e) {
      return String(value);
    }
  }
  return String(value);
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
}

function roleLabel(role) {
  if (role === 'admin') return 'Platform Admin';
  if (role === 'vector_admin_plus') return 'Brand Admin Plus';
  if (role === 'vector_admin') return 'Brand Admin';
  return role || '—';
}

function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [categories, setCategories] = useState({});
  const [actors, setActors] = useState([]);
  const [page, setPage] = useState(0);
  const [canEditNotes, setCanEditNotes] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (categoryFilter) params.category = categoryFilter;
      if (actorFilter) params.actor = actorFilter;
      const res = await fetch(API_ENDPOINTS.adminChangeLogs(params), {
        headers: getBearerAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load change logs');
      }
      setLogs(data.logs || []);
      setTotal(data.total ?? 0);
      setCategories(data.categories || {});
      setActors(Array.isArray(data.actors) ? data.actors : []);
      setCanEditNotes(Boolean(data.canEditNotes));
    } catch (err) {
      setError(err.message);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, actorFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Changing a filter always jumps back to the first page.
  useEffect(() => {
    setPage(0);
  }, [categoryFilter, actorFilter]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openDetail = (item) => {
    setDetailItem(item);
    setNotesDraft(item.adminNotes || '');
    setNotesError('');
  };

  const saveNotes = async () => {
    if (!detailItem || !canEditNotes) return;
    setSavingNotes(true);
    setNotesError('');
    try {
      const res = await fetch(API_ENDPOINTS.adminChangeLogById(detailItem.id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getBearerAuthHeaders(),
        },
        body: JSON.stringify({ adminNotes: notesDraft }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to save notes');
      }
      const updated = data.log;
      setDetailItem(updated);
      setLogs((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    } catch (err) {
      setNotesError(err.message);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="admin-feedback-page admin-logs-page">
      <div className="admin-feedback-content">
        <div className="admin-feedback-header">
          <h1 className="admin-feedback-title">
            <IconList size={24} />
            Admin Logs
          </h1>
          <div className="admin-feedback-filters">
            <select
              className="admin-feedback-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {Object.entries(categories).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select
              className="admin-feedback-select"
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              aria-label="Filter by user"
            >
              <option value="">All users</option>
              {actors.map((a) => (
                <option key={a.email} value={a.email}>{a.name}</option>
              ))}
            </select>
            <button type="button" className="admin-feedback-bulk-btn" onClick={fetchLogs} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>

        <p className="admin-logs-intro">
          Audit trail of Admin and Brand Admin changes to shared brand dashboard settings, strategy data, CSV uploads, and portfolio configuration.
          {canEditNotes ? ' Full admins can add internal notes on each entry.' : ' Notes are read-only for Brand Admin.'}
        </p>

        {error ? (
          <div className="admin-feedback-error">
            <IconAlert size={18} />
            {error}
          </div>
        ) : null}

        {loading ? (
          <p className="admin-feedback-loading">Loading logs...</p>
        ) : logs.length === 0 ? (
          <p className="admin-feedback-empty">No logged changes yet.</p>
        ) : (
          <>
            <p className="admin-feedback-count">
              Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + logs.length} of {total}
            </p>
            <div className="admin-feedback-table-wrap">
              <table className="admin-feedback-table admin-logs-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Category</th>
                    <th>Target</th>
                    <th>Summary</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((item) => (
                    <tr
                      key={item.id}
                      className="admin-feedback-row-clickable"
                      onClick={() => openDetail(item)}
                    >
                      <td className="admin-logs-when">
                        <span className="admin-feedback-date-line">{formatDateTime(item.createdAt)}</span>
                      </td>
                      <td>{item.actorName || item.actorEmail || '—'}</td>
                      <td><span className={`admin-logs-role admin-logs-role-${item.actorRole}`}>{roleLabel(item.actorRole)}</span></td>
                      <td>{item.categoryLabel || item.category}</td>
                      <td className="admin-logs-target" title={item.target || ''}>{item.target || '—'}</td>
                      <td className="admin-logs-summary" title={item.summary}>{item.summary}</td>
                      <td className="admin-logs-notes-preview">{item.adminNotes ? 'Yes' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pageCount > 1 && (
              <div className="admin-logs-pagination">
                <button
                  type="button"
                  className="admin-feedback-bulk-btn"
                  disabled={page <= 0 || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <span className="admin-logs-page-label">Page {page + 1} of {pageCount}</span>
                <button
                  type="button"
                  className="admin-feedback-bulk-btn"
                  disabled={page >= pageCount - 1 || loading}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {detailItem ? (
        <div className="admin-feedback-modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="admin-feedback-modal admin-logs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-feedback-modal-header">
              <h2>Change #{detailItem.id}</h2>
              <button className="admin-feedback-modal-close" onClick={() => setDetailItem(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="admin-feedback-modal-body">
              <div className="admin-feedback-modal-field">
                <strong>When</strong>
                <p>{formatDateTime(detailItem.createdAt)}</p>
              </div>
              <div className="admin-feedback-modal-field">
                <strong>User</strong>
                <p>{detailItem.actorName || detailItem.actorEmail} ({roleLabel(detailItem.actorRole)})</p>
              </div>
              <div className="admin-feedback-modal-field">
                <strong>Category</strong>
                <p>{detailItem.categoryLabel || detailItem.category} · {detailItem.action}</p>
              </div>
              {detailItem.target ? (
                <div className="admin-feedback-modal-field">
                  <strong>Target</strong>
                  <p>{detailItem.target}</p>
                </div>
              ) : null}
              <div className="admin-feedback-modal-field">
                <strong>Summary</strong>
                <p>{detailItem.summary}</p>
              </div>
              {detailItem.changes?.length ? (
                <div className="admin-feedback-modal-field">
                  <strong>Changes</strong>
                  <div className="admin-logs-changes">
                    {detailItem.changes.map((change, idx) => (
                      <div key={`${change.field}-${idx}`} className="admin-logs-change-row">
                        <div className="admin-logs-change-field">{change.field}</div>
                        <div className="admin-logs-change-values">
                          <span className="admin-logs-change-before">{formatValue(change.before)}</span>
                          <span className="admin-logs-change-arrow">→</span>
                          <span className="admin-logs-change-after">{formatValue(change.after)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="admin-feedback-modal-field admin-feedback-modal-edit">
                <strong>Admin notes</strong>
                {canEditNotes ? (
                  <>
                    <textarea
                      className="admin-feedback-modal-notes"
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      rows={4}
                      placeholder="Internal note (admin only)"
                    />
                    {notesError ? <p className="admin-logs-notes-error">{notesError}</p> : null}
                  </>
                ) : (
                  <p className="admin-logs-notes-readonly">{detailItem.adminNotes || '—'}</p>
                )}
              </div>
            </div>
            <div className="admin-feedback-modal-footer">
              <button
                type="button"
                className="admin-feedback-modal-btn admin-feedback-modal-btn-secondary"
                onClick={() => setDetailItem(null)}
              >
                Close
              </button>
              {canEditNotes ? (
                <button
                  type="button"
                  className="admin-feedback-modal-btn admin-feedback-save-btn"
                  onClick={saveNotes}
                  disabled={savingNotes}
                >
                  {savingNotes ? 'Saving…' : 'Save notes'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminLogs;
